/**
 * useAdminClinics — GET /v1/admin/clinics
 *
 * Lists every clinic in the tenant with full admin shape (mips_active,
 * apu_id, reporting_type, submission_method, eligible_clinician_count,
 * specialty_set_codes, etc.). New in backend v0.4.0.
 *
 * Replaces the v1.1.0 admin clinic-activation page's reliance on
 * `GET /v1/clinics` (which returned only ClinicSummary — 4 fields). The
 * V1.2.0 admin page can now do honest read-then-edit instead of a blind
 * "set new values" form.
 *
 * Returns ALL clinics by default (incl. inactive) so admins can activate
 * them. Pass `mipsActive: true` to filter to active only.
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { AdminClinicListResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseAdminClinicsOptions {
  /** When set, filters to mips_active=true or false. Omit for all. */
  mipsActive?: boolean;
  enabled?: boolean;
}

export function useAdminClinics(options: UseAdminClinicsOptions = {}) {
  const { mipsActive, enabled } = options;
  return useQuery({
    queryKey: queryKeys.adminClinics.list(mipsActive),
    queryFn: async (): Promise<AdminClinicListResponse> => {
      const params: Record<string, string | number | boolean> = {};
      if (mipsActive !== undefined) params.mips_active = mipsActive;
      const { data } = await getApiClient().get<AdminClinicListResponse>(
        "/v1/admin/clinics",
        { params: Object.keys(params).length > 0 ? params : undefined },
      );
      return data;
    },
    staleTime: 30_000,
    enabled: enabled ?? true,
  });
}
